import type { QueryClient } from '@tanstack/react-query';

/**
 * Wipe every workspace-scoped cache entry for a previous workspace.
 *
 * Workspace-scoped entities all live under `['workspace', workspaceId, ...]`,
 * so a single prefix-removal sweeps the entire subtree — campaigns, actions,
 * tags, subscription_topics, plus any future workspace-scoped entity that
 * follows the `defineEntity({ scope: 'workspace' })` convention.
 *
 * Account-scoped entities (`['account', accountId, ...]`) and global ones
 * are left untouched, so the user/plan/billing-info caches survive the
 * workspace switch.
 *
 * Wired into the workspace-switch lifecycle by the `authenticated` route's
 * loader in `services/router/create-router.tsx` via
 * `handleWorkspaceTransition` (see `./workspace-cache-sync.ts`). The
 * pilot's `/dev/db/campaigns` "Reset workspace cache" button calls this
 * helper directly for manual exercise.
 *
 * For logout / session reset, also clear account-scoped:
 *
 *   qc.removeQueries({ queryKey: ['workspace'] });
 *   qc.removeQueries({ queryKey: ['account'] });
 */
/** @public */
export function clearWorkspaceQueries(qc: QueryClient, workspaceId: number): void {
  qc.removeQueries({ queryKey: ['workspace', workspaceId] });
}

/** Convenience: clear EVERY workspace's queries (useful for logout). */
/** @public */
export function clearAllWorkspaceQueries(qc: QueryClient): void {
  qc.removeQueries({ queryKey: ['workspace'] });
}

/** Convenience: clear every account-scoped query (logout / session swap). */
/** @public */
export function clearAccountQueries(qc: QueryClient, accountId: number): void {
  qc.removeQueries({ queryKey: ['account', accountId] });
}
